import { ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import {
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
} from "@tabler/icons-react";
import {
  forwardRef,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { twMerge } from "tailwind-merge";
import { AiCreditsLimitBanner } from "../features/billing/aiCreditsLimitBanner.js";
import { BillingBanner } from "../features/billing/billingBanner.js";
import { TrialExpirationBanner } from "../features/billing/trialExpirationBanner.js";
import { AskAlpharunButton } from "../features/copilot/askAlpharunButton.js";
import { CopilotShell } from "../features/copilot/copilotShell.js";
import {
  readStoredBooleanPreference,
  writeStoredBooleanPreference,
} from "../helpers/storedPreference.js";
import {
  createInitialNavLayoutState,
  reduceNavLayoutState,
  selectIsFloatingNavVisible,
  selectIsNavExpanded,
} from "./appLayoutNavState.js";
import { NavCollapseContext } from "./navCollapseContext.js";
import { useNavigationScroll } from "./useNavigationScroll.js";

export interface AppLayoutProps {
  navigation?: ReactNode;
  /** The breadcrumb trail shown in the top bar. */
  breadcrumbs?: ReactNode;
  children?: ReactNode;
}

const floatingNavOpenDelay = 150;
// Delaying the close bridges the gap between the two hover triggers (the toggle
// button and the floating panel) so moving between them doesn't flicker it shut.
const floatingNavCloseDelay = 200;
// After inert is lifted, give the browser a beat to recompute hover before
// handing back to plain hover tracking.
const floatingNavInertReleaseDelay = 100;
// The handoff normally completes on the exit transition's transitionend; this
// padded fallback covers the event never firing (e.g. reduced motion).
const floatingNavHandoffFallbackDelay = 400;

const navCollapsedStorageKey = "alpharun.navCollapsed";

export const AppLayout = forwardRef<HTMLDivElement, AppLayoutProps>(
  (props, ref) => {
    const mainRef = useRef<HTMLElement>(null);

    useNavigationScroll(mainRef);

    const [navState, dispatchNav] = useReducer(
      reduceNavLayoutState,
      undefined,
      () =>
        createInitialNavLayoutState({
          prefersCollapsedNav: readStoredBooleanPreference(
            navCollapsedStorageKey,
          ),
        }),
    );
    const isNavExpanded = selectIsNavExpanded(navState);
    const isFloatingNavVisible = selectIsFloatingNavVisible(navState);

    useEffect(() => {
      writeStoredBooleanPreference(
        navCollapsedStorageKey,
        navState.prefersCollapsedNav,
      );
    }, [navState.prefersCollapsedNav]);

    const floatingNavElementRef = useRef<HTMLDivElement>(null);
    const [floatingNavInertParent, setFloatingNavInertParent] =
      useState<HTMLElement | null>(null);

    useEffect(() => {
      dispatchNav({
        type: "floatingNavInertChanged",
        hasInertParent: floatingNavInertParent !== null,
      });
    }, [floatingNavInertParent]);

    // Timer-driven floating nav phases advance by dispatching an action when
    // their delay elapses, keeping the reducer pure.
    useEffect(() => {
      switch (navState.floatingNavPhase) {
        case "opening": {
          const timeout = setTimeout(() => {
            dispatchNav({ type: "openDelayElapsed" });
          }, floatingNavOpenDelay);
          return () => {
            clearTimeout(timeout);
          };
        }
        case "closing": {
          const timeout = setTimeout(() => {
            // A dropdown opening under the pointer can mark the app inert only
            // after the mouse leave that started this close. Consult the DOM at
            // the moment of the close decision instead of racing that marking.
            const inertParent =
              floatingNavElementRef.current?.closest<HTMLElement>("[inert]") ??
              null;
            if (inertParent) {
              setFloatingNavInertParent(inertParent);
              return;
            }
            dispatchNav({ type: "closeDelayElapsed" });
          }, floatingNavCloseDelay);
          return () => {
            clearTimeout(timeout);
          };
        }
        default:
          return;
      }
    }, [navState.floatingNavPhase]);

    // The handoff completes on the panel's transitionend; this fallback covers
    // the event never firing, immediately when no transition is running at all
    // (e.g. reduced motion).
    useEffect(() => {
      if (navState.floatingNavPhase !== "handingOff") {
        return;
      }

      const panel = floatingNavElementRef.current;
      const hasRunningTransition = (panel?.getAnimations().length ?? 0) > 0;
      const timeout = setTimeout(
        () => {
          dispatchNav({ type: "handoffAnimationCompleted" });
        },
        hasRunningTransition ? floatingNavHandoffFallbackDelay : 0,
      );

      return () => {
        clearTimeout(timeout);
      };
    }, [navState.floatingNavPhase]);

    // Descendants (e.g. full-width editor pages) can request a temporary collapse.
    const requestTemporaryCollapse = useCallback(() => {
      dispatchNav({ type: "temporaryCollapseRequested" });
      return () => {
        dispatchNav({ type: "temporaryCollapseReleased" });
      };
    }, []);
    const navCollapseContextValue = useMemo(
      () => ({ requestTemporaryCollapse }),
      [requestTemporaryCollapse],
    );

    useEffect(() => {
      const window = floatingNavInertParent?.ownerDocument.defaultView;
      if (!window) {
        return;
      }

      let releaseTimeout: number | null = null;
      const cancelRelease = () => {
        if (releaseTimeout !== null) {
          window.clearTimeout(releaseTimeout);
          releaseTimeout = null;
        }
      };

      const mutationObserver = new window.MutationObserver(() => {
        if (floatingNavInertParent.inert) {
          // Inert was re-applied (e.g. the next dropdown opened) before the
          // pending release ran; keep treating the panel as held open.
          cancelRelease();
          return;
        }
        if (releaseTimeout !== null) {
          return;
        }
        releaseTimeout = window.setTimeout(() => {
          releaseTimeout = null;
          // Mouse events don't re-fire for a pointer that never moved, so
          // reconcile hover from the browser's ground truth before releasing.
          dispatchNav({
            type: "floatingNavHoverChanged",
            isHovered:
              floatingNavElementRef.current?.matches(":hover") ?? false,
          });
          setFloatingNavInertParent(null);
        }, floatingNavInertReleaseDelay);
      });
      mutationObserver.observe(floatingNavInertParent, {
        attributes: true,
        attributeFilter: ["inert"],
      });

      return () => {
        mutationObserver.disconnect();
        cancelRelease();
      };
    }, [floatingNavInertParent]);

    return (
      <div
        ref={ref}
        style={{
          // @ts-expect-error: CSS variable is not recognized by TypeScript
          "--ar-nav-width": "200px",
        }}
        className="grid h-full w-full grid-rows-[auto_minmax(0,1fr)] bg-sidebar"
      >
        <div>
          <TrialExpirationBanner />
          <BillingBanner />
          <AiCreditsLimitBanner />
        </div>

        {/* Clips at the padding box, so the copilot panel can slide
            across this gutter and only disappear at the window edge. */}
        <div className="relative grid min-h-0 min-w-0 grid-cols-[auto_1fr_auto] overflow-clip p-2">
          <div
            // The collapsed nav is only visually hidden; inert also removes it
            // from the tab order and accessibility tree. Set via ref because
            // React 18 does not support the `inert` prop.
            ref={(element) => {
              if (element) {
                element.inert = !isNavExpanded;
              }
            }}
            className={twMerge(
              "min-h-0 min-w-0 overflow-clip transition-[width,opacity] duration-300 ease-in-out motion-reduce:transition-none",
              isNavExpanded
                ? "w-[calc(var(--ar-nav-width)+.5rem)] opacity-100"
                : "w-0 opacity-0",
            )}
          >
            <div className="h-full w-(--ar-nav-width)">{props.navigation}</div>
          </div>

          {/* `isolate` keeps the page's own sticky z-indexes (e.g. the
              PageHeader) contained so they can't paint over the floating nav. */}
          <div className="isolate grid min-h-0 min-w-0 grid-rows-[auto_1fr] overflow-clip rounded-2xl bg-white shadow-xs">
            <div className="flex h-11 shrink-0 items-center border-b border-border-light">
              <div className="flex h-full shrink-0 items-center gap-2 pr-2 pl-3">
                <div
                  className="inline-flex"
                  onMouseEnter={() =>
                    dispatchNav({ type: "toggleHoverChanged", isHovered: true })
                  }
                  onMouseLeave={() =>
                    dispatchNav({
                      type: "toggleHoverChanged",
                      isHovered: false,
                    })
                  }
                >
                  <IconButton
                    variant={ButtonVariant.Tertiary}
                    className="hover:bg-muted"
                    accessibleLabel={
                      isNavExpanded
                        ? "Collapse navigation"
                        : "Expand navigation"
                    }
                    icon={
                      isNavExpanded ? (
                        <IconLayoutSidebarLeftCollapse
                          className="size-4"
                          aria-hidden
                        />
                      ) : (
                        <IconLayoutSidebarLeftExpand
                          className="size-4"
                          aria-hidden
                        />
                      )
                    }
                    onClick={() => dispatchNav({ type: "toggleClicked" })}
                  />
                </div>
                <div className="h-5 w-px bg-border-light" aria-hidden />
              </div>

              <div className="flex h-full min-w-0 flex-1 items-center gap-2 pr-4 pl-2">
                {props.breadcrumbs}
              </div>

              <div className="flex h-full shrink-0 items-center pr-2">
                <AskAlpharunButton />
              </div>
            </div>

            <main
              ref={mainRef}
              // Focusable only from code (see `focusMainContent`), so a
              // navigation can hand focus to the page instead of leaving it on
              // the control that triggered it. It's a landmark, not a control,
              // so it takes no focus ring of its own.
              data-main-content
              tabIndex={-1}
              // Use `relative` to reliably create a containing block and prevent positioned descendants (such as those using sr-only) from causing the document to become scrollable
              className="relative h-full w-full overflow-auto focus:outline-none"
            >
              <NavCollapseContext.Provider value={navCollapseContextValue}>
                {props.children}
              </NavCollapseContext.Provider>
            </main>
          </div>

          <CopilotShell />

          <div
            className={twMerge(
              "absolute top-13 bottom-2 left-2 transition-all transition-discrete duration-200 ease-out motion-reduce:transition-none",
              // While closed the panel is `display: none`, taking it out of hit
              // testing entirely; `transition-discrete` defers that until the
              // exit transition finishes, and `starting:` animates the entry.
              isFloatingNavVisible
                ? "block translate-x-0 opacity-100 starting:-translate-x-2 starting:opacity-0"
                : "hidden -translate-x-2 opacity-0",
            )}
            ref={floatingNavElementRef}
            onMouseEnter={() =>
              dispatchNav({ type: "floatingNavHoverChanged", isHovered: true })
            }
            onMouseLeave={() =>
              dispatchNav({ type: "floatingNavHoverChanged", isHovered: false })
            }
            onTransitionEnd={(event) => {
              if (
                event.target === event.currentTarget &&
                event.propertyName === "opacity"
              ) {
                dispatchNav({ type: "handoffAnimationCompleted" });
              }
            }}
          >
            <div className="h-full overflow-clip rounded-2xl bg-sidebar p-2 shadow-lg outline-1 outline-border-light">
              <div className="h-full w-(--ar-nav-width)">
                {props.navigation}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);
